// The contract between otto-app and the ConsultSession Durable Object class
// that otto-live defines. otto-app reaches the class through a Durable Object
// binding with script_name "otto-live" (design 3.2 row 10) and calls these
// RPC methods; the browser reaches the same object through otto-live's
// WebSocket route.
//
// Naming: one object per consult, idFromName(consult_id). Both Workers must
// use consultSessionName() so they land on the same object.
//
// otto-app pushes everything the call needs at check-in (init), so the live
// path never reads a database: the DO holds the guest context, the pinned
// config bundle and the switch snapshot in its own SQLite (design 3.1).
import { z } from "zod";
import { CoachFlag, ConfigBundle, ModelPrice, Switches } from "@otto/config";
import { Ledger, TranscriptMessage } from "@otto/package-schema";
import { EndReason, Mode } from "@otto/protocol";
import { IsoTime } from "./common";

export function consultSessionName(consultId: string): string {
  return consultId;
}

/**
 * How long after check-in the owner may START the call (the first hello).
 * After this the consult is abandoned: otto-app's sweep marks it so and asks
 * the object to expire, and the object refuses a late first hello on its own
 * too. Both Workers use this one number (review fix, Sept 28 2026: the live
 * token lasts 4 hours, so a check-in could otherwise be banked for hours and
 * started past the caps and the switches).
 */
export const FIRST_CONNECT_WINDOW_MINUTES = 30;

/**
 * Only what Otto may know about the guest (design 5.7), plus the industry
 * from check-in (the list or the owner's "Other" words, as quoted data):
 * nothing else from check-in.
 */
export const GuestContext = z.object({
  first_name: z.string().min(1).max(100),
  business_name: z.string().min(1).max(200),
  industry: z.string().max(200).nullable().default(null),
  channel_label: z.string().max(80).nullable(),
  language: z.enum(["en", "es"]),
  mode: Mode,
});
export type GuestContext = z.infer<typeof GuestContext>;

/**
 * A consult that continues an unfinished one (Oct 18 2026, Wes: a dropped call
 * can be finished from the emailed link). The object starts with the earlier
 * conversation in its transcript (so every voice session is seeded with it),
 * the earlier ledger and flags, and the clock where it stopped.
 */
export const ResumeSeed = z.object({
  from_consult_id: z.uuid(),
  messages: z.array(TranscriptMessage).max(800),
  ledger: Ledger,
  flags: z.array(CoachFlag),
  clock_seconds: z.number().int().min(0),
  owner_talk_seconds: z.number().int().min(0),
});
export type ResumeSeed = z.infer<typeof ResumeSeed>;

export const ConsultInit = z.object({
  consult_id: z.uuid(),
  created_at: IsoTime,
  is_test: z.boolean(),
  guest: GuestContext,
  /** Pinned at check-in; never changes mid-call (design 9.11). */
  bundle: ConfigBundle,
  /** Switches as they were at check-in. */
  switches: Switches,
  /** Per consult spend thresholds in force (design 9.5). */
  spend: z.object({
    notice_usd: z.number().min(0),
    alert_usd: z.number().min(0),
    close_usd: z.number().min(0),
    /**
     * The voice model's price at check-in, entered by a person in the admin
     * (otto-app's price table). null: voice is unpriced, so in-call voice cost
     * is unknown and the per consult cap acts on known brain costs only.
     */
    voice_price: ModelPrice.nullable().default(null),
  }),
  /** Set when this consult continues an unfinished one; null for a new consult. */
  resume: ResumeSeed.nullable().default(null),
});
export type ConsultInit = z.infer<typeof ConsultInit>;

export const LivePhase = z.enum(["empty", "initialized", "live", "disconnected", "ended"]);

export const LiveSnapshot = z.object({
  consult_id: z.uuid().nullable(),
  phase: LivePhase,
  connected: z.boolean(),
  started_at: IsoTime.nullable(),
  clock_seconds: z.number().int().min(0),
  stage: z.string().nullable(),
  agenda_index: z.number().int().min(0).nullable(),
  mode: Mode.nullable(),
  cost_so_far_usd: z.number().min(0).nullable(),
  end_reason: EndReason.nullable(),
});
export type LiveSnapshot = z.infer<typeof LiveSnapshot>;

export type InitResult = { ok: true } | { ok: false; error: "invalid" | "already_started" };

/**
 * RPC surface of ConsultSession. otto-live's class implements it; otto-app
 * calls it. Arguments and results must be structured-clone friendly.
 */
export interface ConsultSessionRpc {
  /** Store guest context, bundle and switches. Idempotent until the call starts. */
  init(input: ConsultInit): Promise<InitResult>;
  /** For "Live now" and the thank-you page. Cheap; never touches the voice engine. */
  snapshot(): Promise<LiveSnapshot>;
  /** Admin or cap driven end. Otto gets a close note; the DO still ends after his last word. */
  forceEnd(reason: EndReason): Promise<{ ended: boolean }>;
  /**
   * A check-in that never started (the sweep abandoned it): the object drops
   * the guest's details and refuses any later hello. No effect once the call
   * has started (`expired: false`).
   */
  expire(): Promise<{ expired: boolean }>;
}
